import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

const BENEFITS = [
  {
    icon: 'ri-map-pin-2-line',
    title: '20 Acres of Hilltop Beauty',
    description:
      'Perched on a scenic hilltop just 20 minutes from Fort Worth, our grounds offer breathtaking natural beauty as a backdrop for your event.',
  },
  {
    icon: 'ri-home-heart-line',
    title: 'Exclusively Yours All Day',
    description:
      'We host only one event per day. You and your guests have the entire venue to yourselves — complete privacy, undivided attention, no clock watching.',
  },
  {
    icon: 'ri-group-line',
    title: 'Up to 300 Guests',
    description:
      'Over 6,000 sq ft of climate-controlled space and 3,500 sq ft of covered patio — room for intimate gatherings and grand celebrations alike.',
  },
  {
    icon: 'ri-sparkling-2-line',
    title: 'Thoughtfully Designed',
    description:
      'From elegant bridal suites to a grand staircase entrance, lush landscaping, and full-service amenities — every detail was crafted with love by Tracy and Vernon Chapa.',
  },
];

export default function WhyChooseUs() {
  const { ref: leftRef, isVisible: leftVisible } = useInView();
  const { ref: rightRef, isVisible: rightVisible } = useInView({ threshold: 0.1 });

  return (
    <section className="bg-rdr-charcoal overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
        {/* Image — left */}
        <div
          ref={leftRef as React.RefObject<HTMLDivElement>}
          className={`relative overflow-hidden min-h-[400px] lg:min-h-0 scale-in ${leftVisible ? 'is-visible' : ''}`}
        >
          <img
            src="https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/1631653889244-4L86Y1JQ4I8PA1YERL3S/FA606739-06AC-4820-A84F-631482186EA5.jpeg"
            alt="Luxury wedding ceremony setup at Red Door Ranch Joshua TX"
            className="w-full h-full object-cover object-top"
          />
          {/* Overlay badge */}
          <div className="absolute bottom-8 left-8 bg-rdr-dark/80 backdrop-blur-sm border border-rdr-gold/30 px-6 py-4">
            <p className="text-rdr-gold font-sans text-xs tracking-[0.2em] uppercase mb-1">Family Owned</p>
            <p className="text-white font-serif text-xl">Tracy &amp; Vernon Chapa</p>
          </div>
        </div>

        {/* Content — right */}
        <div
          ref={rightRef as React.RefObject<HTMLDivElement>}
          className={`py-16 lg:py-20 px-8 lg:px-14 xl:px-20 flex flex-col justify-center slide-right ${rightVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">Why Red Door Ranch</p>
          </div>
          <h2 className="font-serif text-white text-4xl lg:text-5xl xl:text-6xl mb-5 leading-tight">
            An Experience Unlike Any Other Venue
          </h2>
          <p className="text-white/55 font-sans text-base leading-relaxed mb-12 max-w-sm">
            Built with passion by owners Tracy and Vernon Chapa, Red Door Ranch is more than a venue —
            it&apos;s the realization of a dream, and the beginning of yours.
          </p>

          <div className="space-y-8 mb-12">
            {BENEFITS.map((benefit, i) => (
              <div key={i} className="flex gap-5">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border border-rdr-gold/30 bg-rdr-gold/5">
                  <i className={`${benefit.icon} text-lg text-rdr-gold`} />
                </div>
                <div>
                  <h3 className="font-serif text-white text-lg mb-1.5 leading-snug">{benefit.title}</h3>
                  <p className="text-white/45 font-sans text-sm leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-6">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-rdr-gold font-sans text-xs tracking-[0.2em] uppercase border-b border-rdr-gold/50 pb-1 hover:border-rdr-gold transition-colors duration-300 cursor-pointer"
            >
              Our Story
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </Link>
            <Link
              to="/location#contact"
              className="inline-block bg-rdr-gold/15 border border-rdr-gold/40 text-rdr-gold font-sans text-xs tracking-[0.2em] uppercase px-6 py-3 hover:bg-rdr-gold hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              Book a Tour
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
